import React, { useEffect, useMemo, useState } from 'react';
import {
  Briefcase,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Hash,
  Inbox,
  Layers,
  PenLine,
  TimerOff,
  UserCheck,
  Users,
} from 'lucide-react';
import { useSiteContext } from '../../SiteContext';
import FloatMenuButton, { FloatMenuOption } from '../../components/v2/FloatMenuButton';
import FilterBar, { FilterEmpty } from '../../components/FilterBar';
import Highlight from '../../components/Highlight';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import { PayPeriod } from '../../interfaces/PayPeriodInterface';
import { getCrewWorkDetail, getCrewWorkList, getCrewWorkSupervisors } from '../../api/crewWork';
import {
  CrewWorkDetail,
  CrewWorkJob,
  CrewWorkList,
  CrewWorkListRow,
  CrewWorkSupervisorOption,
} from '../../interfaces/CrewWork';
import {
  CrewScope,
  FilteredCrewEmployee,
  accountOptions,
  crewScopeNote,
  filterCrewEmployees,
  filterSupervisorList,
  flaggedPunchCount,
  jobOptions,
  normalizeTerm,
} from '../../util/crewWorkFilter';
import './CrewWorkPage.css';

/*
 * Crew Work (Wallcraft) — supervisor-first view of the week's hourly punches.
 *
 * Wallcraft has no supervisor→project relationship, so the 2020PH Project Work page shows
 * nothing useful there. This page starts from the supervisor (any active employee with an
 * active direct report) and walks their crew: employee → job → day → punch, the same data
 * the CREW_PROJECT_WORK email sends. Read-only. Week comes from the header pill.
 *
 * Presentation helpers below are copied from ProjectWorkPage.tsx (pw- → cw-) rather than
 * shared, so neither page can drift the other.
 */

// ─── Helpers ───────────────────────────────────────────────────────────────

/** Minutes → decimal hours. Punch-level rows use two places so a 4.25h segment reads true. */
const hrs = (minutes: number, places = 2): string => (minutes / 60).toFixed(places);

const initials = (name: string): string => {
  const parts = name.trim().split(/[\s,]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (parts[0]?.slice(0, 2) ?? '?').toUpperCase();
};

/** Minutes since local midnight -> "7:04a" / "12:07p"; "(open)" for a punch with no out yet. */
const fmtPunchTime = (minutes: number | null): string => {
  if (minutes == null) return '(open)';
  const h24 = Math.floor(minutes / 60) % 24;
  const mm = String(minutes % 60).padStart(2, '0');
  return `${h24 % 12 || 12}:${mm}${h24 >= 12 ? 'p' : 'a'}`;
};

/** "Mon" + "6/22" for the punch table's date cell. */
const fmtPunchDate = (iso: string): { dow: string; md: string } => {
  const d = new Date(iso.slice(0, 10) + 'T12:00:00Z');
  const dows = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return { dow: dows[d.getUTCDay()], md: `${d.getUTCMonth() + 1}/${d.getUTCDate()}` };
};

const fmtShort = (iso: string): string => {
  const d = new Date(iso.slice(0, 10) + 'T12:00:00Z');
  const mons = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${mons[d.getUTCMonth()]} ${d.getUTCDate()}`;
};

const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

/** HTTP status off an axios rejection, if any. */
const statusOf = (e: unknown): number | undefined =>
  (e as { response?: { status?: number } })?.response?.status;

// ─── Small presentational pieces ─────────────────────────────────────────────

/** Overlay spinner that sits on top of stale content (no layout shift on reload). */
const LoadingOverlay: React.FC = () => (
  <div className="cw-overlay">
    <div className="cw-spinner" />
    <span>Loading…</span>
  </div>
);

/** Centered spinner for first loads (when there is no prior content to keep). */
const CenteredLoader: React.FC<{ label?: string }> = ({ label = 'Loading…' }) => (
  <div className="cw-loading">
    <div className="cw-spinner" />
    <span>{label}</span>
  </div>
);

/** An In or Out cell: a plain time, or the struck original above the corrected value. */
const PunchTime: React.FC<{ minutes: number | null; original?: number }> = ({ minutes, original }) => {
  if (original == null) return <>{fmtPunchTime(minutes)}</>;
  return (
    <>
      <span className="cw-punch-was">{fmtPunchTime(original)}</span>
      <span className="cw-punch-now">{fmtPunchTime(minutes)}</span>
    </>
  );
};

// ─── Punch table (one job) ───────────────────────────────────────────────────

/**
 * One job's punches, grouped by day. Meals are their own muted rows and never add to a
 * total; open punches count 0. Flags are independent — a punch can be Manual, Edited and
 * Open at once, and shows every badge that applies.
 */
const JobBlock: React.FC<{ job: CrewWorkJob; term: string; scopeNote?: string }> = ({ job, term, scopeNote }) => (
  <div className="cw-job">
    <div className="cw-job-head">
      <span className="cw-job-label">
        <Briefcase size={12} /> <Highlight text={job.label} term={term} />
      </span>
      <span className="cw-job-total">
        {hrs(job.workedMinutes)} h · {plural(job.punchCount, 'punch', 'punches')}
        {job.openPunchCount > 0 && ` · ${job.openPunchCount} open`}
        {scopeNote && <span className="cw-scope-note"> · {scopeNote}</span>}
      </span>
    </div>
    <div className="cw-linetable-wrap cw-punchtable-wrap">
      <table className="cw-linetable cw-punchtable">
        <thead>
          <tr>
            <th>Date</th>
            <th>In</th>
            <th>Out</th>
            <th>Detail</th>
            <th className="cw-r">Hours</th>
          </tr>
        </thead>
        <tbody>
          {job.days.map((day) => {
            const { dow, md } = fmtPunchDate(day.date);
            return day.punches.map((p, i) => {
              const rowClass = [
                'cw-punch-row',
                p.type === 'meal' ? 'cw-punch-meal' : '',
                p.edited ? 'cw-punch-edited' : p.manual ? 'cw-punch-manual' : '',
                p.open ? 'cw-punch-open' : '',
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <tr key={p.timeEntryHourlyID} className={rowClass}>
                  {i === 0 && (
                    <td className="cw-punch-date" rowSpan={day.punches.length}>
                      <span className="cw-punch-dow">{dow}</span>
                      <span className="cw-punch-md">{md}</span>
                      <span className="cw-punch-daytot">
                        {hrs(day.workedMinutes)} h
                        {day.mealMinutes > 0 ? ` · ${hrs(day.mealMinutes)} h meal` : ''}
                      </span>
                    </td>
                  )}
                  <td className="cw-punch-time">
                    <PunchTime minutes={p.inMinutes} original={p.originalInMinutes} />
                  </td>
                  <td className="cw-punch-time">
                    <PunchTime minutes={p.outMinutes} original={p.originalOutMinutes} />
                  </td>
                  <td className="cw-punch-detail">
                    <div className="cw-punch-detail-inner">
                      <span className={`cw-punch-chip cw-punch-chip-${p.type}`}>
                        {p.type === 'meal' ? 'Meal' : 'Work'}
                      </span>
                      {p.accountName && (
                        <span className="cw-punch-acct">
                          <Highlight text={p.accountName} term={term} />
                        </span>
                      )}
                      {p.poNumber && (
                        <span className="cw-po-chip">
                          PO <Highlight text={p.poNumber} term={term} />
                        </span>
                      )}
                      {p.manual && <span className="cw-punch-flag cw-punch-flag-manual">Manual</span>}
                      {p.edited && <span className="cw-punch-flag cw-punch-flag-edited">Edited</span>}
                      {p.open && <span className="cw-punch-flag cw-punch-flag-open">Open</span>}
                    </div>
                  </td>
                  <td className="cw-r cw-punch-hrs">
                    {p.type === 'meal' ? (
                      <span className="cw-muted">{hrs(p.minutes)}</span>
                    ) : p.open ? (
                      <span className="cw-muted">—</span>
                    ) : (
                      hrs(p.workedMinutes)
                    )}
                  </td>
                </tr>
              );
            });
          })}
          <tr className="cw-punch-row cw-punch-tot">
            <td className="cw-punch-date" />
            <td colSpan={3}>Job total · worked time</td>
            <td className="cw-r cw-punch-hrs">{hrs(job.workedMinutes)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
);

// ─── Employee row (detail accordion) ─────────────────────────────────────────

const rowHasDetail = (row: FilteredCrewEmployee): boolean => row.jobs.length > 0;

const EmployeeRow: React.FC<{
  row: FilteredCrewEmployee;
  term: string;
  scopeNote?: string;
  open: boolean;
  onToggle: () => void;
}> = ({ row, term, scopeNote, open, onToggle }) => {
  const { emp, jobs } = row;
  const hasDetail = rowHasDetail(row);
  const noTime = (emp.jobs ?? []).length === 0;

  // Sub-line counts describe the employee, not the current filter — the tables narrow.
  const nEdits = flaggedPunchCount(emp);
  const subBits: string[] = [];
  if (noTime) subBits.push('No time this week');
  else {
    subBits.push(plural(emp.daysWorked, 'day'));
    subBits.push(plural(emp.jobs.length, 'job'));
    if (nEdits > 0) subBits.push(plural(nEdits, 'edit'));
    if (emp.openPunchCount > 0) subBits.push(`${emp.openPunchCount} open`);
  }

  return (
    <div
      className={`cw-emp ${open ? 'open' : ''}${hasDetail ? '' : ' cw-emp-inert'}${
        row.direct ? ' fmatch' : row.deepOnly ? ' fctx' : ''
      }`}
    >
      <button
        className="cw-emp-main"
        onClick={hasDetail ? onToggle : undefined}
        aria-expanded={hasDetail ? open : undefined}
      >
        <div className="cw-emp-id">
          <span className="cw-avatar">{initials(emp.name)}</span>
          <span className="cw-emp-name">
            <span>
              <Highlight text={emp.name} term={term} />
              {!emp.active && <span className="cw-inactive">Inactive</span>}
            </span>
            <span className="cw-emp-alt">
              <Highlight text={emp.altEmployeeID} term={term} />
            </span>
            <span className={`cw-emp-sub${noTime ? ' cw-emp-sub-none' : ''}`}>{subBits.join(' · ')}</span>
          </span>
        </div>
        <div className="cw-emp-val cw-muted">{noTime ? '—' : emp.jobs.length}</div>
        <div className={`cw-emp-val ${noTime ? 'cw-muted' : 'cw-hourly'}`}>
          {noTime ? '—' : `${hrs(emp.workedMinutes)} h`}
        </div>
        {hasDetail ? <ChevronRight size={16} className="cw-emp-caret" /> : <span />}
      </button>

      {open && hasDetail && (
        <div className="cw-emp-detail">
          {jobs.map((j) => (
            <JobBlock key={j.key} job={j} term={term} scopeNote={scopeNote} />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Supervisor row (landing) ────────────────────────────────────────────────

const SupervisorRow: React.FC<{ row: CrewWorkListRow; term: string; onOpen: () => void }> = ({
  row,
  term,
  onOpen,
}) => {
  const flags: string[] = [];
  if (row.openPunchCount > 0) flags.push(`${row.openPunchCount} open`);
  if (row.editedPunchCount > 0) flags.push(`${row.editedPunchCount} edited`);
  return (
    <button className="cw-sup-row" onClick={onOpen}>
      <div className="cw-sr-name">
        <span className="cw-sr-id">
          <Highlight text={row.altEmployeeID} term={term} />
        </span>
        <span className="cw-sr-title">
          <Highlight text={row.name} term={term} />
        </span>
        <span className="cw-sr-meta">
          <Users size={12} /> {plural(row.directReportCount, 'direct report')} · {row.employeesWithTime} with time
        </span>
      </div>
      <div className="cw-sr-metric">
        <span className="cw-sr-mval cw-hourly">{hrs(row.workedMinutes, 1)} h</span>
        <span className="cw-sr-mlabel">HOURS</span>
      </div>
      <div className="cw-sr-metric">
        <span className="cw-sr-mval">{row.jobCount}</span>
        <span className="cw-sr-mlabel">JOBS</span>
      </div>
      <div className="cw-sr-metric cw-sr-flags">
        <span className={`cw-sr-mval ${flags.length ? 'cw-warn' : 'cw-muted'}`}>
          {flags.length ? flags.join(' · ') : '—'}
        </span>
        <span className="cw-sr-mlabel">OPEN / EDITED</span>
      </div>
      <div className="cw-sr-go">
        <ChevronRight size={18} />
      </div>
    </button>
  );
};

// ─── Main page ───────────────────────────────────────────────────────────────

const SCOPE_CHIPS: [CrewScope, string][] = [
  ['all', 'All'],
  ['open', 'Open punches'],
  ['flagged', 'Edited or manual'],
];

const CrewWorkPage: React.FC = () => {
  // The week comes from the header pill (SiteContext) — no picker of its own.
  const { payPeriods, payPeriodID } = useSiteContext();
  const currentPP: PayPeriod | undefined = payPeriods.find((p) => p.PayPeriodID === payPeriodID);

  const [supervisors, setSupervisors] = useState<CrewWorkSupervisorOption[] | null>(null);
  const [selectedSupID, setSelectedSupID] = useState<number | null>(null);

  const [list, setList] = useState<CrewWorkList | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [detail, setDetail] = useState<CrewWorkDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const [expandedEmps, setExpandedEmps] = useState<Set<number>>(new Set());

  // Landing search — resets when the week's list is refetched.
  const [supFilterText, setSupFilterText] = useState('');
  const supTerm = normalizeTerm(useDebouncedValue(supFilterText));

  // Detail filters — survive week navigation, reset on supervisor change.
  const [crewFilter, setCrewFilter] = useState('');
  const [scope, setScope] = useState<CrewScope>('all');
  const [jobKey, setJobKey] = useState<string | null>(null);
  const [accountKey, setAccountKey] = useState<string | null>(null);
  const crewTerm = normalizeTerm(useDebouncedValue(crewFilter));

  // A single supervisor in scope (typically a super viewing their own crew) skips the landing.
  const single = supervisors != null && supervisors.length === 1;
  const view: 'list' | 'detail' = selectedSupID != null ? 'detail' : 'list';

  useEffect(() => {
    getCrewWorkSupervisors()
      .then(setSupervisors)
      .catch(() => setSupervisors([]));
  }, []);

  const clearFilters = () => {
    setCrewFilter('');
    setScope('all');
    setJobKey(null);
    setAccountKey(null);
  };

  const openSupervisor = (employeeID: number) => {
    setSelectedSupID(employeeID);
    setDetail(null);
    clearFilters();
    window.scrollTo({ top: 0 });
  };

  const backToList = () => {
    setSelectedSupID(null);
    clearFilters();
    window.scrollTo({ top: 0 });
  };

  useEffect(() => {
    if (single && selectedSupID == null && supervisors) openSupervisor(supervisors[0].employeeID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [single]);

  // Landing list per week (skipped for the single-supervisor case).
  useEffect(() => {
    if (!payPeriodID || supervisors == null || single) return;
    let cancelled = false;
    setListLoading(true);
    setListError(null);
    setSupFilterText('');
    getCrewWorkList(payPeriodID)
      .then((data) => {
        if (!cancelled) setList(data);
      })
      .catch((e) => {
        if (cancelled) return;
        setList(null);
        setListError(
          statusOf(e) === 404
            ? 'Crew Work is not available for this week (pay period not found, or not enabled for this site).'
            : statusOf(e) === 403
              ? "You don't have access to Crew Work."
              : 'Could not load crew activity.'
        );
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [payPeriodID, supervisors, single]);

  // Detail per (supervisor, week).
  useEffect(() => {
    if (selectedSupID == null || !payPeriodID) return;
    let cancelled = false;
    setDetailLoading(true);
    setDetailError(null);
    setExpandedEmps(new Set());
    getCrewWorkDetail(selectedSupID, payPeriodID)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch((e) => {
        if (cancelled) return;
        const status = statusOf(e);
        setDetail(null);
        setDetailError(
          status === 403
            ? "You don't have access to this supervisor's crew."
            : status === 404
              ? 'Supervisor or pay period not found — this employee may no longer be a supervisor.'
              : 'Could not load crew detail.'
        );
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedSupID, payPeriodID]);

  // ── Derived views ─────────────────────────────────────────────────────────
  const supView = useMemo(() => filterSupervisorList(list?.supervisors ?? [], supTerm), [list, supTerm]);

  // Landing KPIs — the list has no totals object; each crew member has one SupervisorID,
  // so summing the rows never double-counts.
  const listTotals = useMemo(() => {
    const rows = list?.supervisors ?? [];
    return {
      supervisorCount: rows.length,
      employeesWithTime: rows.reduce((a, r) => a + r.employeesWithTime, 0),
      workedMinutes: rows.reduce((a, r) => a + r.workedMinutes, 0),
      openPunchCount: rows.reduce((a, r) => a + r.openPunchCount, 0),
    };
  }, [list]);

  const jobOpts = useMemo(() => jobOptions(detail?.employees ?? []), [detail]);
  // A job picked in another week may not exist in this one — treat it as "all jobs".
  const effectiveJobKey = jobKey != null && jobOpts.some((o) => o.key === jobKey) ? jobKey : null;

  const accountOpts = useMemo(() => accountOptions(detail?.employees ?? []), [detail]);
  // Same fallback as the job: an account picked in another week may be absent in this one.
  const effectiveAccountKey =
    accountKey != null && accountOpts.some((o) => o.key === accountKey) ? accountKey : null;
  const accountValueLabel =
    effectiveAccountKey == null
      ? 'All accounts'
      : accountOpts.find((o) => o.key === effectiveAccountKey)?.label ?? 'All accounts';

  const crewView = useMemo(
    () => filterCrewEmployees(detail?.employees ?? [], crewTerm, scope, effectiveJobKey, effectiveAccountKey),
    [detail, crewTerm, scope, effectiveJobKey, effectiveAccountKey]
  );
  const scopeNote = crewScopeNote(scope, effectiveAccountKey == null ? undefined : accountValueLabel);

  // Rows whose hit is buried in a job open themselves; clearing the term leaves them open.
  useEffect(() => {
    if (crewView.revealIDs.length === 0) return;
    setExpandedEmps((prev) => new Set([...prev, ...crewView.revealIDs]));
  }, [crewView]);

  const expandableEmpIDs = useMemo(
    () => crewView.rows.filter(rowHasDetail).map((r) => r.emp.employeeID),
    [crewView]
  );
  const allEmpsExpanded = expandableEmpIDs.length > 0 && expandableEmpIDs.every((id) => expandedEmps.has(id));
  const noEmpsExpanded = !expandableEmpIDs.some((id) => expandedEmps.has(id));
  const expandAllEmps = () => setExpandedEmps(new Set(expandableEmpIDs));
  const collapseAllEmps = () => setExpandedEmps(new Set());
  const toggleEmp = (employeeID: number) =>
    setExpandedEmps((prev) => {
      const next = new Set(prev);
      next.has(employeeID) ? next.delete(employeeID) : next.add(employeeID);
      return next;
    });

  const weekNo = list?.weekNumber ?? currentPP?.WeekNumber;
  const weekLong = weekNo != null ? `Week ${weekNo}` : '—';

  // Super picker: on the landing it opens that supervisor; on detail it switches.
  const supMenuOptions: FloatMenuOption[] = useMemo(
    () => (supervisors ?? []).map((s) => ({ value: String(s.employeeID), label: `${s.name} (${s.altEmployeeID})` })),
    [supervisors]
  );
  const selectedSup = (supervisors ?? []).find((s) => s.employeeID === selectedSupID);
  const SuperPicker =
    supervisors && supervisors.length > 1 ? (
      <FloatMenuButton
        label="Super"
        valueLabel={selectedSup ? selectedSup.name : 'Choose a supervisor'}
        isPlaceholder={!selectedSup}
        isActive={!!selectedSup}
        options={supMenuOptions}
        selectedValue={selectedSupID != null ? String(selectedSupID) : ''}
        onSelect={(v) => v !== '' && openSupervisor(Number(v))}
        searchable
      />
    ) : null;

  const jobMenuOptions: FloatMenuOption[] = useMemo(
    () => [
      { value: '', label: 'All jobs' },
      ...jobOpts.map((o) => ({ value: o.key, label: o.label, count: o.employeeCount })),
    ],
    [jobOpts]
  );
  const jobValueLabel =
    effectiveJobKey == null ? 'All jobs' : jobOpts.find((o) => o.key === effectiveJobKey)?.label ?? 'All jobs';
  const accountMenuOptions = useMemo<FloatMenuOption[]>(
    () => [
      { value: '', label: 'All accounts' },
      ...accountOpts.map((o) => ({ value: o.key, label: o.label, count: o.employeeCount })),
    ],
    [accountOpts]
  );

  // ─── Render ───
  return (
    <div className="cw">
      {view === 'list' ? (
        <>
          <div className="cw-head">
            <div className="cw-head-icon">
              <Users size={24} />
            </div>
            <div className="cw-head-main">
              <h1 className="cw-title">Crew Work</h1>
              <div className="cw-subline">
                <span className="cw-sub-desc">
                  Each supervisor's crew — direct reports' hourly punches for {weekLong}.
                </span>
              </div>
            </div>
          </div>

          {SuperPicker && (
            <div className="cw-filterbar">
              {SuperPicker}
              <span className="cw-filter-note">
                {list ? plural(list.supervisors.length, 'supervisor') : ''}
              </span>
            </div>
          )}

          <div className="cw-body">
            {listLoading && list && <LoadingOverlay />}

            {list ? (
              <>
                <div className="cw-kpis">
                  <div className="cw-kpi">
                    <div className="cw-kpi-label">
                      <UserCheck size={14} /> SUPERVISORS
                    </div>
                    <div className="cw-kpi-val">{listTotals.supervisorCount}</div>
                  </div>
                  <div className="cw-kpi">
                    <div className="cw-kpi-label">
                      <Users size={14} /> CREW WITH TIME
                    </div>
                    <div className="cw-kpi-val">{listTotals.employeesWithTime}</div>
                  </div>
                  <div className="cw-kpi">
                    <div className="cw-kpi-label cw-green">
                      <Clock size={14} /> HOURS
                    </div>
                    <div className="cw-kpi-val">{hrs(listTotals.workedMinutes, 1)}</div>
                  </div>
                  <div className="cw-kpi">
                    <div className="cw-kpi-label cw-warn">
                      <TimerOff size={14} /> OPEN PUNCHES
                    </div>
                    <div className="cw-kpi-val">{listTotals.openPunchCount}</div>
                  </div>
                </div>

                <div className="cw-sect">
                  <span>
                    <Layers size={14} /> SUPERVISORS · {weekLong.toUpperCase()}
                  </span>
                  <span className="cw-sect-note">tap a row for the crew's punches</span>
                </div>

                {list.supervisors.length === 0 ? (
                  <div className="cw-empty">
                    <Inbox size={26} />
                    <div className="cw-empty-title">No supervisors</div>
                    <div className="cw-empty-sub">No supervisors with an active crew are in your scope.</div>
                  </div>
                ) : (
                  <>
                    <div className="cw-list-filter">
                      <FilterBar
                        value={supFilterText}
                        onChange={setSupFilterText}
                        placeholder="Filter supervisors — name or employee #…"
                        label="Filter supervisors"
                        matches={supView.matches}
                        total={list.supervisors.length}
                        active={supTerm !== ''}
                      />
                    </div>
                    {supView.supervisors.length === 0 ? (
                      <div className="cw-list-nomatch">
                        <FilterEmpty noun="supervisors" term={supTerm} onClear={() => setSupFilterText('')} />
                      </div>
                    ) : (
                      <div className="cw-sup-list">
                        {supView.supervisors.map((s) => (
                          <SupervisorRow
                            key={s.employeeID}
                            row={s}
                            term={supTerm}
                            onOpen={() => openSupervisor(s.employeeID)}
                          />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </>
            ) : listError ? (
              <div className="cw-error">{listError}</div>
            ) : supervisors != null && supervisors.length === 0 && !listLoading ? (
              <div className="cw-empty">
                <Inbox size={26} />
                <div className="cw-empty-title">No supervisors</div>
                <div className="cw-empty-sub">No supervisors with an active crew are in your scope.</div>
              </div>
            ) : (
              <CenteredLoader />
            )}
          </div>
        </>
      ) : (
        <>
          {(!single || SuperPicker) && (
            <div className="cw-backbar">
              {!single && (
                <button className="cw-back" onClick={backToList}>
                  <ChevronLeft size={16} /> All supervisors
                </button>
              )}
              {SuperPicker}
            </div>
          )}

          {!detail && detailLoading && <CenteredLoader label="Loading crew…" />}
          {!detail && !detailLoading && detailError && <div className="cw-error">{detailError}</div>}

          {detail && (
            <>
              <div className="cw-head">
                <div className="cw-head-icon">
                  <Users size={24} />
                </div>
                <div className="cw-head-main">
                  <h1 className="cw-title">
                    {detail.supervisor.name}{' '}
                    <span className="cw-title-alt">{detail.supervisor.altEmployeeID}</span>
                  </h1>
                  <div className="cw-subline">
                    <span className="cw-sub-desc">
                      {plural(detail.supervisor.directReportCount, 'direct report')} ·
                      Week {detail.weekNumber} · {fmtShort(detail.weekStartDate)} – {fmtShort(detail.weekEndDate)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="cw-body">
                {detailLoading && <LoadingOverlay />}

                <div className="cw-kpis cw-kpis-5">
                  <div className="cw-kpi">
                    <div className="cw-kpi-label">
                      <Users size={14} /> CREW WITH TIME
                    </div>
                    <div className="cw-kpi-val">
                      {detail.totals.employeesWithTime}
                      <span className="cw-kpi-of"> of {detail.totals.crewCount}</span>
                    </div>
                  </div>
                  <div className="cw-kpi">
                    <div className="cw-kpi-label cw-green">
                      <Clock size={14} /> HOURS
                    </div>
                    <div className="cw-kpi-val">{hrs(detail.totals.workedMinutes, 1)}</div>
                  </div>
                  <div className="cw-kpi">
                    <div className="cw-kpi-label">
                      <Hash size={14} /> JOBS
                    </div>
                    <div className="cw-kpi-val">{detail.totals.jobCount}</div>
                  </div>
                  <div className="cw-kpi">
                    <div className="cw-kpi-label cw-warn">
                      <TimerOff size={14} /> OPEN
                    </div>
                    <div className="cw-kpi-val">{detail.totals.openPunchCount}</div>
                  </div>
                  <div className="cw-kpi">
                    <div className="cw-kpi-label">
                      <PenLine size={14} /> EDITED / MANUAL
                    </div>
                    <div className="cw-kpi-val">
                      {detail.totals.editedPunchCount}
                      <span className="cw-kpi-of"> / {detail.totals.manualPunchCount}</span>
                    </div>
                  </div>
                </div>

                <div className="cw-sect">
                  <span>
                    <Users size={14} /> CREW · {detail.totals.employeesWithTime} with time
                  </span>
                  <div className="cw-sect-actions">
                    <span className="cw-sect-note">tap a row for jobs &amp; punches</span>
                    {expandableEmpIDs.length > 0 && (
                      <>
                        <button
                          className="cw-btn-sm"
                          type="button"
                          onClick={expandAllEmps}
                          disabled={allEmpsExpanded}
                        >
                          <ChevronDown size={14} /> Expand all
                        </button>
                        <button
                          className="cw-btn-sm"
                          type="button"
                          onClick={collapseAllEmps}
                          disabled={noEmpsExpanded}
                        >
                          <ChevronRight size={14} /> Collapse all
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div className="cw-emp-card">
                  {detail.employees.length > 0 && (
                    <FilterBar
                      value={crewFilter}
                      onChange={setCrewFilter}
                      placeholder="Filter crew — name, ID, job, account, PO #…"
                      label="Filter crew"
                      matches={crewView.rows.length}
                      total={detail.employees.length}
                      active={
                        crewTerm !== '' || scope !== 'all' || effectiveJobKey != null || effectiveAccountKey != null
                      }
                    >
                      <div className="cw-fb-controls">
                        {jobOpts.length > 1 && (
                          <FloatMenuButton
                            label="Job"
                            valueLabel={jobValueLabel}
                            isPlaceholder={effectiveJobKey == null}
                            isActive={effectiveJobKey != null}
                            options={jobMenuOptions}
                            selectedValue={effectiveJobKey ?? ''}
                            onSelect={(v) => setJobKey(v === '' ? null : v)}
                            searchable
                          />
                        )}
                        {accountOpts.length > 1 && (
                          <FloatMenuButton
                            label="Account"
                            valueLabel={accountValueLabel}
                            isPlaceholder={effectiveAccountKey == null}
                            isActive={effectiveAccountKey != null}
                            options={accountMenuOptions}
                            selectedValue={effectiveAccountKey ?? ''}
                            onSelect={(v) => setAccountKey(v === '' ? null : v)}
                            searchable
                          />
                        )}
                        {/* A scope, not a search: cuts rows, days and punches at once. */}
                        <div className="fb-chips" role="radiogroup" aria-label="Punch scope">
                          {SCOPE_CHIPS.map(([kind, label]) => (
                            <button
                              key={kind}
                              type="button"
                              role="radio"
                              aria-checked={scope === kind}
                              className={`fb-chip${scope === kind ? ' on' : ''}`}
                              onClick={() => setScope(kind)}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </FilterBar>
                  )}
                  <div className="cw-emp-head">
                    <span>Employee</span>
                    <span className="cw-r">Jobs</span>
                    <span className="cw-r">Hours</span>
                    <span />
                  </div>
                  {detail.employees.length === 0 ? (
                    <div className="cw-empty cw-empty-flat">
                      <Inbox size={26} />
                      <div className="cw-empty-title">No crew</div>
                      <div className="cw-empty-sub">This supervisor has no active direct reports.</div>
                    </div>
                  ) : crewView.rows.length === 0 ? (
                    <FilterEmpty
                      noun="crew members"
                      term={crewTerm}
                      onClear={clearFilters}
                    />
                  ) : (
                    crewView.rows.map((r) => (
                      <EmployeeRow
                        key={r.emp.employeeID}
                        row={r}
                        term={crewTerm}
                        scopeNote={scopeNote}
                        open={expandedEmps.has(r.emp.employeeID)}
                        onToggle={() => toggleEmp(r.emp.employeeID)}
                      />
                    ))
                  )}
                  <div className="cw-emp-foot">
                    <span>Totals</span>
                    <span className="cw-r cw-muted">{detail.totals.jobCount}</span>
                    <span className="cw-r cw-hourly">{hrs(detail.totals.workedMinutes)} h</span>
                    <span />
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default CrewWorkPage;
